import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Typography,
  message,
  Tag,
  Badge,
  Spin,
  Card,
  Row,
  Col,
  Input,
  Checkbox,
  Tooltip
} from 'antd';
import { EyeOutlined, ShoppingCartOutlined, SettingOutlined, CheckCircleOutlined, SearchOutlined, ClearOutlined, LockOutlined, InboxOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { customerOrderAPI, productStockLockAPI } from '../services/api.ts';
import apiClient from '../utils/axiosConfig.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { CustomerOrder, OrderCompleteness } from '../types.ts';
import { formatCurrency } from '../utils/numberFormatting.ts';
import StockReservationsModal from './StockReservationsModal.tsx';
import MultiSourceReservationModal from './MultiSourceReservationModal.tsx';
import StockOrderCoverageModal from './StockOrderCoverageModal.tsx';
import ReserveAvailableChoiceModal, { ReserveAvailableProduct, productLabel } from './ReserveAvailableChoiceModal.tsx';

const { Title, Text } = Typography;

interface InventoryStock {
  id: number;
  product_id: number;
  customer_id?: number;
  supplier_id?: number;
  stock_code?: string;
  date_stock?: string;
  entry_date?: string;
  customer_order_id?: number;
  supplier_order_id?: number;
  supplier_order_date?: string;
  warehouse_id?: number;
  udc_pallet?: string;
  udc_collo?: string;
  qta_available: number;
  qta_reserved: number;
  qta_in_production: number;
  qta_in_transit: number;
  total_quantity: number;
  qta_residuo?: number;
  cost?: number;
  price?: number;
  // Campi leggibili
  internal_product_code: string;
  client_product_code?: string;
  product_description: string;
  size?: string;
  color?: string;
  customer_company?: string;
  supplier_name?: string;
  warehouse_description: string;
  // Prenotazioni totali (tutti gli ordini)
  total_reservations: number;
  total_orders_with_reservations: number;
  has_reservations: boolean;
  available_reservations: number;
  in_transit_reservations: number;
  in_production_reservations: number;
  // Prenotazioni specifiche di questo ordine
  current_order_available_reservations: number;
  current_order_in_transit_reservations: number;
  current_order_in_production_reservations: number;
  current_order_total_reservations: number;
  supplier_delivery_date?: string;
  supplier_estimated_delivery_date?: string;
  supplier_order_status?: string;
  expected_availability_date?: string;
  // Campi container
  container_code?: string;
  container_description?: string;
  departure_date?: string;
  actual_departure_date?: string;
  expected_arrival_date?: string;
  actual_arrival_date?: string;
  data_partenza?: string;
  data_prevista_arrivo?: string;
  // Campi documento doganale
  weight_gross?: number;
  customs_document_number?: string;
  cdi_dogana_code?: string;
  cdi_singolo?: number;
  // Informazioni specifiche dell'ordine
  order_requested_quantity: number;
  order_unit_price?: number;
  // Lock information
  locked_by?: number;
  locked_at?: string;
  locked_by_username?: string;
}

interface OrderManagementModalProps {
  visible: boolean;
  onClose: () => void;
  order: CustomerOrder | null;
  onOrderUpdated?: () => void;
}

const OrderManagementModal: React.FC<OrderManagementModalProps> = ({
  visible,
  onClose,
  order,
  onOrderUpdated
}) => {
  const { user: currentUser, canWrite } = useAuth();
  const canEdit = canWrite('customer-orders');
  const [loading, setLoading] = useState(false);
  const [inventoryData, setInventoryData] = useState<InventoryStock[]>([]);
  const [reservationsModalVisible, setReservationsModalVisible] = useState(false);
  const [multiSourceModalVisible, setMultiSourceModalVisible] = useState(false);
  const [autoReservingStockId, setAutoReservingStockId] = useState<number | null>(null);
  const [coverageVisible, setCoverageVisible] = useState(false);
  // Opziona tutti i disponibili
  const [bulkReserving, setBulkReserving] = useState(false);
  const [bulkAskVisible, setBulkAskVisible] = useState(false);
  const [bulkChoiceVisible, setBulkChoiceVisible] = useState(false);
  const [bulkChoiceProducts, setBulkChoiceProducts] = useState<ReserveAvailableProduct[]>([]);
  const [bulkSummary, setBulkSummary] = useState({ products: 0, quantity: 0, autoProducts: 0, autoQuantity: 0, notReservable: [] as ReserveAvailableProduct[] });
  const [selectedStock, setSelectedStock] = useState<InventoryStock | null>(null);
  const [orderCompleteness, setOrderCompleteness] = useState<OrderCompleteness | null>(null);
  const [pageSize, setPageSize] = useState(100);
  const productInputRef = useRef<any>(null);
  const udcInputRef = useRef<any>(null);
  const docInputRef = useRef<any>(null);
  const [filterKey, setFilterKey] = useState(0);
  const [appliedFilterProduct, setAppliedFilterProduct] = useState('');
  const [appliedFilterUdc, setAppliedFilterUdc] = useState('');
  const [appliedFilterDoc, setAppliedFilterDoc] = useState('');
  const [filterReservations, setFilterReservations] = useState(false);
  const [filterAvailability, setFilterAvailability] = useState(true);

  useEffect(() => {
    if (visible && order) {
      loadOrderInventory();
      loadOrderCompleteness();
    }
  }, [visible, order]);

  // Safety net: rilascia il lock se l'utente chiude il browser/tab
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (selectedStock && multiSourceModalVisible) {
        const token = localStorage.getItem('token');
        // URL dal client API (prima 'http://localhost:8000' cablato: in produzione il rilascio
        // del lock non arrivava mai). fetch con keepalive: axios non lo supporta su beforeunload
        fetch(`${apiClient.defaults.baseURL}/product-stocks/${selectedStock.id}/lock`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          keepalive: true
        }).catch(() => {});
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [selectedStock, multiSourceModalVisible]);

  // Cleanup lock quando OrderManagementModal si chiude
  useEffect(() => {
    if (!visible && selectedStock && multiSourceModalVisible) {
      productStockLockAPI.releaseLock(selectedStock.id).catch(() => {});
      setMultiSourceModalVisible(false);
      setSelectedStock(null);
    }
  }, [visible]);

  const loadOrderInventory = async () => {
    if (!order) return;
    
    setLoading(true);
    try {
      const data = await customerOrderAPI.getOrderInventory(order.id);
      setInventoryData(data.items);
    } catch (error) {
      console.error('Error loading order inventory:', error);
      message.error('Errore nel caricamento dell\'inventario ordine');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderCompleteness = async () => {
    if (!order) return;
    
    try {
      const data = await customerOrderAPI.checkOrderCompleteness(order.id);
      setOrderCompleteness(data);
    } catch (error) {
      console.error('Error loading order completeness:', error);
    }
  };

  const handleViewReservations = (stock: InventoryStock) => {
    setSelectedStock(stock);
    setReservationsModalVisible(true);
  };

  const handleReserveProducts = async (stock: InventoryStock) => {
    if (orderClosed) {
      message.warning('Ordine evaso, in spedizione o annullato: le prenotazioni non sono più modificabili');
      return;
    }
    try {
      await productStockLockAPI.acquireLock(stock.id);
      setSelectedStock(stock);
      setMultiSourceModalVisible(true);
    } catch (error: any) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        const lockedByUsername = typeof detail === 'object' ? detail.locked_by_username : 'un altro utente';
        message.warning(`Stock in uso dall'utente ${lockedByUsername}. Riprova tra qualche minuto.`);
      } else {
        message.error('Errore durante il blocco dello stock');
      }
    }
  };

  // Pezzi del DISPONIBILE di questa giacenza che "Opziona Disponibili" prenoterebbe:
  // quota libera della riga (disponibile - prenotazioni di tutti gli ordini), limitata
  // a quanto manca all'ordine per quel prodotto. Transito e produzione non si toccano.
  const getAutoReserveQuantity = (stock: InventoryStock): number => {
    const free = Math.max(0, (stock.qta_available || 0) - (stock.available_reservations || 0));
    const requested = stock.order_requested_quantity;
    if (requested === undefined || requested === null || requested <= 0) return 0;
    const missing = Math.max(0, requested - (productReservationTotals[stock.product_id] || 0));
    return Math.min(free, missing);
  };

  const submitAutoReserve = async (stock: InventoryStock, quantity: number, forceCrossCustomer: boolean) => {
    if (!order) return;
    try {
      await customerOrderAPI.updateProductReservations(order.id, stock.product_id, stock.id, {
        // valori ASSOLUTI per questa giacenza: si aggiunge solo al disponibile,
        // transito e produzione restano quelli già prenotati dall'ordine
        available: (stock.current_order_available_reservations || 0) + quantity,
        in_transit: stock.current_order_in_transit_reservations || 0,
        in_production: stock.current_order_in_production_reservations || 0,
        unit_price: stock.order_unit_price || stock.price || 0,
        force_cross_customer: forceCrossCustomer || undefined,
      });
      message.success(`Opzionati ${quantity} pz disponibili`);
      loadOrderInventory();
      loadOrderCompleteness();
    } catch (error: any) {
      // 409 = giacenza dedicata a un altro cliente: chiedi conferma e riprova col flag
      if (error.response?.status === 409 && !forceCrossCustomer) {
        Modal.confirm({
          title: 'Giacenza dedicata a un altro cliente',
          content: error.response?.data?.detail,
          okText: 'Prenota comunque',
          okButtonProps: { danger: true },
          cancelText: 'Annulla',
          onOk: () => handleAutoReserve(stock, true),
        });
        return;
      }
      message.error(error.response?.data?.detail || "Errore nell'opzione automatica dei prodotti disponibili");
    }
  };

  const handleAutoReserve = async (stock: InventoryStock, forceCrossCustomer = false) => {
    const quantity = getAutoReserveQuantity(stock);
    if (quantity <= 0) return;
    setAutoReservingStockId(stock.id);
    let locked = false;
    try {
      await productStockLockAPI.acquireLock(stock.id);
      locked = true;
      await submitAutoReserve(stock, quantity, forceCrossCustomer);
    } catch (error: any) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        const lockedByUsername = typeof detail === 'object' ? detail.locked_by_username : 'un altro utente';
        message.warning(`Stock in uso dall'utente ${lockedByUsername}. Riprova tra qualche minuto.`);
      } else {
        message.error('Errore durante il blocco dello stock');
      }
    } finally {
      if (locked) {
        productStockLockAPI.releaseLock(stock.id).catch(() => {});
      }
      setAutoReservingStockId(null);
    }
  };

  // Ordine evaso, in spedizione o annullato: niente nuove prenotazioni
  const orderClosed = !!order && (order.order_status_id >= 6 || order.order_status_id === 99);

  const renderNotReservable = (items: ReserveAvailableProduct[]) => (
    <ul style={{ paddingLeft: 18, margin: '8px 0 0' }}>
      {items.map(p => (
        <li key={p.product_id}>
          <Text strong>{productLabel(p)}</Text>: {p.skipped_reason}
        </li>
      ))}
    </ul>
  );

  const executeBulkReserve = async (allocations?: Record<number, Record<number, number>>) => {
    if (!order) return;
    setBulkReserving(true);
    try {
      const result = await customerOrderAPI.reserveAvailable(order.id, allocations);
      setBulkAskVisible(false);
      setBulkChoiceVisible(false);
      if (result.total_reserved > 0) {
        message.success(`Opzionati ${result.total_reserved} pz disponibili su ${result.products.length} prodotti`);
      } else {
        message.info('Nessun pezzo opzionato');
      }
      loadOrderInventory();
      loadOrderCompleteness();
      if (onOrderUpdated) onOrderUpdated();
    } catch (error: any) {
      message.error(error.response?.data?.detail || "Errore nell'opzione dei prodotti disponibili");
    } finally {
      setBulkReserving(false);
    }
  };

  const handleBulkReserve = async () => {
    if (!order) return;
    setBulkReserving(true);
    let preview: any;
    try {
      preview = await customerOrderAPI.reserveAvailablePreview(order.id);
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Errore nel calcolo dei prodotti disponibili');
      setBulkReserving(false);
      return;
    }
    setBulkReserving(false);

    const products: ReserveAvailableProduct[] = preview.products || [];
    const toReserve = products.filter(p => p.to_reserve > 0);
    const notReservable = products.filter(p => p.missing > 0 && p.to_reserve <= 0);

    if (toReserve.length === 0) {
      Modal.info({
        title: 'Nessun prodotto da opzionare',
        width: 640,
        content: notReservable.length > 0
          ? <div>Non ci sono pezzi disponibili liberi per i prodotti che mancano.{renderNotReservable(notReservable)}</div>
          : "Tutti i prodotti dell'ordine sono già completamente prenotati.",
      });
      return;
    }

    const choice = toReserve.filter(p => p.needs_choice);
    const auto = toReserve.filter(p => !p.needs_choice);
    const summary = {
      products: toReserve.length,
      quantity: toReserve.reduce((sum, p) => sum + p.to_reserve, 0),
      autoProducts: auto.length,
      autoQuantity: auto.reduce((sum, p) => sum + p.to_reserve, 0),
      notReservable,
    };
    setBulkSummary(summary);

    if (choice.length === 0) {
      Modal.confirm({
        title: 'Opziona tutti i disponibili',
        width: 640,
        content: (
          <div>
            Verranno opzionati <b>{summary.quantity} pz</b> disponibili su <b>{summary.products} prodotti</b>.
            Transito, produzione, merce già prenotata e merce dedicata ad altri clienti non vengono toccati.
            {notReservable.length > 0 && (
              <div style={{ marginTop: 8 }}>Prodotti non opzionabili:{renderNotReservable(notReservable)}</div>
            )}
          </div>
        ),
        okText: 'Opziona',
        cancelText: 'Annulla',
        onOk: () => executeBulkReserve(),
      });
      return;
    }

    setBulkChoiceProducts(choice);
    setBulkAskVisible(true);
  };

  const handleCloseReservationsModal = () => {
    setReservationsModalVisible(false);
    setSelectedStock(null);
  };

  const handleCloseMultiSourceModal = async () => {
    if (selectedStock) {
      try {
        await productStockLockAPI.releaseLock(selectedStock.id);
      } catch (error) {
        // Non bloccare la chiusura della modal per un errore di unlock
      }
    }
    setMultiSourceModalVisible(false);
    setSelectedStock(null);
  };

  const handleReservationComplete = async () => {
    // Rilascia il lock dopo conferma prenotazione
    if (selectedStock) {
      try {
        await productStockLockAPI.releaseLock(selectedStock.id);
      } catch (error) {
        // Non bloccare il completamento per un errore di unlock
      }
    }
    // Ricarica l'inventario per riflettere le nuove prenotazioni
    loadOrderInventory();
    loadOrderCompleteness();
    if (onOrderUpdated) {
      onOrderUpdated();
    }
  };

  // Genera identificatore composto per prodotto
  const generateCompositeIdentifier = (internal_code: string, size?: string, color?: string) => {
    if (!size && !color) {
      return internal_code;
    } else if (size && !color) {
      return `${internal_code}-${size}`;
    } else if (!size && color) {
      return `${internal_code}--${color}`;
    } else {
      return `${internal_code}-${size}-${color}`;
    }
  };

  const renderQuantityWithReservations = (
    type: 'available' | 'in_transit' | 'in_production',
    stock: InventoryStock
  ) => {
    let totalQuantity = 0;
    let totalReservations = 0;
    let currentOrderReservations = 0;
    let color = 'default';

    if (type === 'available') {
      totalQuantity = stock.qta_available;
      totalReservations = stock.available_reservations;
      currentOrderReservations = stock.current_order_available_reservations;
      color = totalQuantity > 0 ? 'green' : 'default';
    }
    else if (type === 'in_transit') {
      // Mostra qta_in_transit per prodotti in transito
      totalQuantity = stock.qta_in_transit;
      totalReservations = stock.in_transit_reservations;
      currentOrderReservations = stock.current_order_in_transit_reservations;
      color = totalQuantity > 0 ? 'orange' : 'default';
    }
    else if (type === 'in_production') {
      // Mostra qta_in_production per prodotti in produzione
      totalQuantity = stock.qta_in_production;
      totalReservations = stock.in_production_reservations;
      currentOrderReservations = stock.current_order_in_production_reservations;
      color = totalQuantity > 0 ? 'blue' : 'default';
    }

    // Calcola la differenza (quantità presente - quantità prenotata totale)
    const difference = totalQuantity - totalReservations;

    return (
      <Space direction="vertical" size="small" style={{ textAlign: 'center' }}>
        {/* Riga 1: Totale con prenotazioni totali */}
        <Tag color={color}>
          {totalReservations > 0 
            ? `${totalQuantity} (di cui Opz. ${totalReservations})`
            : totalQuantity.toString()
          }
        </Tag>
        
        {/* Riga 2: Differenza disponibile */}
        <div style={{ fontSize: '11px', color: '#666' }}>
          Differenza: {difference}
        </div>
        
        {/* Riga 3: Prenotazioni di questo ordine */}
        <div style={{ fontSize: '11px', color: currentOrderReservations > 0 ? '#1890ff' : '#999' }}>
          Prenotati: {currentOrderReservations}
        </div>

        {/* Data di disponibilità se presente */}
        {totalQuantity > 0 && stock.expected_availability_date && type !== 'available' && (
          <Tag color="purple" style={{ fontSize: '10px' }}>
            {type === 'in_transit' ? 'Arrivo' : 'Consegna'}: {new Date(stock.expected_availability_date).toLocaleDateString('it-IT')}
          </Tag>
        )}
      </Space>
    );
  };

  const handleApplyFilters = () => {
    setAppliedFilterProduct(productInputRef.current?.input?.value || '');
    setAppliedFilterUdc(udcInputRef.current?.input?.value || '');
    setAppliedFilterDoc(docInputRef.current?.input?.value || '');
  };

  const handleClearFilters = () => {
    setFilterKey(prev => prev + 1);
    setAppliedFilterProduct('');
    setAppliedFilterUdc('');
    setAppliedFilterDoc('');
    setFilterReservations(false);
    setFilterAvailability(true);
  };

  // Aggregazione prenotazioni per product_id (somma su tutti gli stock)
  const productReservationTotals = useMemo(() => {
    const totals: Record<number, number> = {};
    inventoryData.forEach(item => {
      totals[item.product_id] = (totals[item.product_id] || 0) + (item.current_order_total_reservations || 0);
    });
    return totals;
  }, [inventoryData]);

  const filteredData = useMemo(() => {
    let data = inventoryData;
    const prodFilter = appliedFilterProduct.toLowerCase().trim();
    const udcFilter = appliedFilterUdc.toLowerCase().trim();
    const docFilter = appliedFilterDoc.toLowerCase().trim();

    if (prodFilter) {
      // Come una LIKE '%testo%': parte del testo basta (es. "0692CUF184-T000"),
      // e * o % dentro il testo valgono "qualsiasi sequenza" (es. "0692*CBLU")
      const escaped = prodFilter.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/[*%]/g, '.*');
      const pattern = new RegExp(escaped);
      const matches = (v?: string) => pattern.test((v || '').toLowerCase());
      data = data.filter(item =>
        matches(generateCompositeIdentifier(item.internal_product_code || '', item.size, item.color)) ||
        matches(item.internal_product_code) ||
        matches(item.client_product_code) ||
        matches(item.product_description) ||
        matches(item.size) ||
        matches(item.color)
      );
    }
    if (udcFilter) {
      data = data.filter(item =>
        (item.udc_pallet || '').toLowerCase().includes(udcFilter) ||
        (item.udc_collo || '').toLowerCase().includes(udcFilter)
      );
    }
    if (docFilter) {
      data = data.filter(item =>
        (item.customs_document_number || '').toLowerCase().includes(docFilter)
      );
    }
    if (filterReservations) {
      data = data.filter(item => item.current_order_total_reservations > 0);
    }
    if (filterAvailability) {
      data = data.filter(item =>
        (item.qta_available || 0) + (item.qta_in_transit || 0) + (item.qta_in_production || 0) > 0
      );
    }
    return data;
  }, [inventoryData, appliedFilterProduct, appliedFilterUdc, appliedFilterDoc, filterReservations, filterAvailability]);

  const columns = [
    {
      title: 'Prodotto',
      key: 'product',
      width: 280,
      render: (record: InventoryStock) => {
        const compositeIdentifier = generateCompositeIdentifier(
          record.internal_product_code,
          record.size,
          record.color
        );

        const totalReservedForProduct = productReservationTotals[record.product_id] || 0;
        const remaining = record.order_requested_quantity - totalReservedForProduct;

        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{compositeIdentifier}</div>
            {record.client_product_code && (
              <div style={{ fontSize: '13px', color: '#333', marginTop: '2px' }}>
                {record.client_product_code}
              </div>
            )}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {record.product_description}
            </div>
            <div style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px' }}>
              <strong>Richiesto: {record.order_requested_quantity}</strong>
              {' | '}
              <span style={{ color: totalReservedForProduct > 0 ? '#52c41a' : '#999' }}>
                Prenotato: {totalReservedForProduct}
              </span>
              {' | '}
              <span style={{ color: remaining > 0 ? '#ff4d4f' : '#52c41a', fontWeight: 'bold' }}>
                {remaining > 0 ? `Da prenotare: ${remaining}` : 'Completo'}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: 'UDC',
      key: 'udc',
      width: 140,
      align: 'center' as const,
      render: (_: any, record: InventoryStock) => {
        if (!record.udc_pallet && !record.udc_collo) {
          return <Tag color="default">Nessuno</Tag>;
        }
        return (
          <Space direction="vertical" size="small">
            {record.udc_pallet && <Tag color="blue">Pallet: {record.udc_pallet}</Tag>}
            {record.udc_collo && <Tag color="cyan">Collo: {record.udc_collo}</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Disponibile',
      key: 'qta_available',
      width: 150,
      align: 'center' as const,
      render: (record: InventoryStock) =>
        renderQuantityWithReservations('available', record),
    },
    {
      title: 'In Transito',
      key: 'in_transit',
      width: 150,
      align: 'center' as const,
      render: (record: InventoryStock) =>
        renderQuantityWithReservations('in_transit', record),
    },
    {
      title: 'In Produzione',
      key: 'in_production',
      width: 150,
      align: 'center' as const,
      render: (record: InventoryStock) =>
        renderQuantityWithReservations('in_production', record),
    },
    {
      title: 'Data ingresso',
      dataIndex: 'entry_date',
      key: 'entry_date',
      width: 110,
      align: 'center' as const,
      sorter: (a: InventoryStock, b: InventoryStock) =>
        new Date(a.entry_date || 0).getTime() - new Date(b.entry_date || 0).getTime(),
      render: (_: any, record: InventoryStock) => {
        if (!record.entry_date) return '-';
        const source = record.date_stock
          ? 'Data stock'
          : record.supplier_order_date
            ? 'Data ordine fornitore'
            : 'Data creazione riga';
        return (
          <Tooltip title={`Fonte: ${source}`}>
            <span>{new Date(record.entry_date).toLocaleDateString('it-IT')}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Vendita',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      align: 'right' as const,
      render: (value: number) => value ? `€ ${formatCurrency(value)}` : '-',
    },
    {
      title: 'Costo',
      dataIndex: 'cost',
      key: 'cost',
      width: 80,
      align: 'right' as const,
      sorter: (a: InventoryStock, b: InventoryStock) => (a.cost || 0) - (b.cost || 0),
      render: (value: number) => value ? `€ ${formatCurrency(value)}` : '-',
    },
    {
      title: 'Peso lordo a pz',
      dataIndex: 'weight_gross',
      key: 'weight_gross',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value ? `${value.toLocaleString('it-IT', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} kg` : '-',
    },
    {
      title: 'Doc. carico prodotto',
      dataIndex: 'customs_document_number',
      key: 'customs_document_number',
      width: 140,
      render: (text: string) => text || '-',
    },
    {
      title: 'Codice doganale',
      dataIndex: 'cdi_dogana_code',
      key: 'cdi_dogana_code',
      width: 130,
      render: (text: string) => text || '-',
    },
    {
      title: 'Singolo',
      dataIndex: 'cdi_singolo',
      key: 'cdi_singolo',
      width: 80,
      align: 'right' as const,
      render: (value: number) => value != null ? value : '-',
    },
    {
      title: 'Magazzino',
      dataIndex: 'warehouse_description',
      key: 'warehouse_description',
      width: 120,
    },
    {
      title: 'Cliente',
      dataIndex: 'customer_company',
      key: 'customer_company',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Stock Code',
      dataIndex: 'stock_code',
      key: 'stock_code',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Container',
      key: 'container',
      width: 180,
      render: (record: InventoryStock) => {
        if (!record.container_code && !record.container_description) {
          return '-';
        }

        return (
          <div style={{ fontSize: '12px' }}>
            {record.container_code && (
              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                Codice: {record.container_code}
              </div>
            )}
            {record.container_description && (
              <div style={{ marginBottom: '2px' }}>
                {record.container_description}
              </div>
            )}
            {record.data_partenza && (
              <div style={{ color: '#1890ff', marginBottom: '1px' }}>
                Partenza: {new Date(record.data_partenza).toLocaleDateString('it-IT')}
              </div>
            )}
            {record.data_prevista_arrivo && (
              <div style={{ color: '#52c41a' }}>
                Arrivo: {new Date(record.data_prevista_arrivo).toLocaleDateString('it-IT')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Fornitore',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 120,
      render: (text: string) => text || '-',
    },
    {
      title: 'Azioni',
      key: 'actions',
      fixed: 'right' as const,
      width: 250,
      render: (record: InventoryStock) => {
        // locked_at è UTC senza suffisso 'Z': letto come ora locale risultava vecchio di 2 h
        // in Italia e "In uso" non compariva mai (si scopriva solo dal 409 al clic)
        const lockedAtMs = (s: string) => new Date(/Z|[+-]\d\d:\d\d$/.test(s) ? s : s + 'Z').getTime();
        const isLockedByOther = record.locked_by &&
          record.locked_by !== currentUser?.id &&
          record.locked_at &&
          (new Date().getTime() - lockedAtMs(record.locked_at)) < 5 * 60 * 1000;

        return (
          <Space size="small">
            {record.has_reservations && (
              <Badge count={record.total_orders_with_reservations} size="small">
                <Button
                  type="default"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleViewReservations(record)}
                  title="Visualizza prenotazioni"
                />
              </Badge>
            )}
            <Button
              type="primary"
              size="small"
              icon={isLockedByOther ? <LockOutlined /> : <ShoppingCartOutlined />}
              onClick={() => handleReserveProducts(record)}
              disabled={orderClosed}
              title={orderClosed
                ? 'Ordine evaso, in spedizione o annullato: prenotazioni non modificabili'
                : isLockedByOther
                ? `In uso da ${record.locked_by_username}`
                : "Opziona prodotti"}
              style={isLockedByOther ? { backgroundColor: '#faad14', borderColor: '#faad14' } : {}}
            >
              {isLockedByOther ? 'In uso' : 'Opziona'}
            </Button>
            {(() => {
              const autoQty = getAutoReserveQuantity(record);
              return (
                <Button
                  type="primary"
                  size="small"
                  icon={<ThunderboltOutlined />}
                  loading={autoReservingStockId === record.id}
                  disabled={orderClosed || isLockedByOther || autoQty <= 0 || autoReservingStockId !== null || bulkReserving}
                  onClick={() => handleAutoReserve(record)}
                  title={autoQty > 0
                    ? `Opziona subito i ${autoQty} pz disponibili di questa riga (transito e produzione non vengono toccati)`
                    : 'Nessun pezzo disponibile da opzionare su questa riga (già prenotati, o ordine già coperto)'}
                  style={autoQty > 0 && !isLockedByOther ? { backgroundColor: '#52c41a', borderColor: '#52c41a' } : {}}
                >
                  {autoQty > 0 ? `+${autoQty}` : 'Disp.'}
                </Button>
              );
            })()}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <SettingOutlined style={{ marginRight: 8 }} />
              Gestione Ordine
            </Title>
            {order && (
              <Text type="secondary">
                Ordine: {order.order_number}{order.customer_order_number ? ` (${order.customer_order_number})` : ''} | Cliente: {order.customer?.company || 'N/A'}
              </Text>
            )}
          </div>
        }
        open={visible}
        onCancel={onClose}
        footer={
          <Button onClick={onClose}>
            Chiudi
          </Button>
        }
        width="100%"
        style={{ top: 10, padding: '0 12px' }}
      >
        <Spin spinning={loading}>
          {/* Riepilogo Ordine */}
          {order && (
            <Card size="small" style={{ marginBottom: '16px', backgroundColor: '#f0f2f5' }}>
              <Row gutter={[16, 8]}>
                <Col span={6}>
                  <Text strong>Numero Ordine: </Text>
                  <br />
                  <Text>{order.order_number}</Text>
                </Col>
                <Col span={6}>
                  <Text strong>Data Ordine: </Text>
                  <br />
                  <Text>{order.order_date ? new Date(order.order_date).toLocaleDateString('it-IT') : 'N/A'}</Text>
                </Col>
                <Col span={6}>
                  <Text strong>Data Consegna a Cliente: </Text>
                  <br />
                  <Text>{order.requested_delivery_date ? new Date(order.requested_delivery_date).toLocaleDateString('it-IT') : 'N/A'}</Text>
                </Col>
                <Col span={6}>
                  <Text strong>Status: </Text>
                  <br />
                  <Tag color="blue">{order.order_status?.status_name || 'N/A'}</Tag>
                </Col>
              </Row>
              <Row gutter={[16, 8]} style={{ marginTop: '8px' }} align="bottom">
                <Col span={6}>
                  <Text strong>Prodotti Inventario: </Text>
                  <br />
                  <Tag color="green">{inventoryData.length}</Tag>
                </Col>
                {canEdit && !orderClosed && (
                  <Col span={18} style={{ textAlign: 'right' }}>
                    <Tooltip title="Prenota il disponibile libero di tutti i prodotti dell'ordine, fino alla quantità che manca. Transito, produzione, merce già prenotata da altri ordini e merce dedicata ad altri clienti sono esclusi.">
                      <Button
                        type="primary"
                        icon={<ThunderboltOutlined />}
                        loading={bulkReserving}
                        disabled={autoReservingStockId !== null}
                        onClick={handleBulkReserve}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      >
                        Opziona tutti i disponibili
                      </Button>
                    </Tooltip>
                  </Col>
                )}
              </Row>
              {order.reorder && (
                <Row gutter={[16, 8]} style={{ marginTop: '8px' }}>
                  <Col span={24}>
                    <Space>
                      <Tag color="orange">ORDINE STOCK</Tag>
                      <Text type="secondary">
                        L'evadibilità dipende dalla merce ENTRATA a fronte dell'ordine, non dalla giacenza attuale.
                      </Text>
                      <Button size="small" type="primary" icon={<InboxOutlined />} onClick={() => setCoverageVisible(true)}>
                        Copertura Ordine Stock
                      </Button>
                    </Space>
                  </Col>
                </Row>
              )}
              {orderCompleteness && (
                <Row gutter={[16, 8]} style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #d9d9d9' }}>
                  <Col span={8}>
                    <Text strong>{order.reorder ? 'Copertura Ordine: ' : 'Completezza Ordine: '}</Text>
                    <br />
                    <Tag color={orderCompleteness.can_fulfill ? 'green' : 'orange'}>
                      {Math.round(orderCompleteness.total_completion_percentage)}%
                    </Tag>
                  </Col>
                  <Col span={8}>
                    <Text strong>Stato Evasione: </Text>
                    <br />
                    {orderCompleteness.can_fulfill ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        PRONTO PER EVASIONE
                      </Tag>
                    ) : (
                      <Tag color="orange">
                        IN PREPARAZIONE
                      </Tag>
                    )}
                  </Col>
                  <Col span={8}>
                    <Text strong>Prodotti Completi: </Text>
                    <br />
                    <Tag color="blue">
                      {orderCompleteness.products_completeness.filter(p => p.is_complete).length}/{orderCompleteness.products_completeness.length}
                    </Tag>
                  </Col>
                </Row>
              )}
            </Card>
          )}

          {/* Filtri */}
          <Row gutter={12} style={{ marginBottom: 12 }} align="middle">
            <Col flex="auto">
              <Row gutter={12}>
                <Col span={8}>
                  <Input
                    key={`prod-${filterKey}`}
                    ref={productInputRef}
                    placeholder="Prodotto (codice-taglia-colore, descrizione; * = jolly)"
                    prefix={<SearchOutlined />}
                    onPressEnter={handleApplyFilters}
                  />
                </Col>
                <Col span={8}>
                  <Input
                    key={`udc-${filterKey}`}
                    ref={udcInputRef}
                    placeholder="UDC (pallet o collo)"
                    prefix={<SearchOutlined />}
                    onPressEnter={handleApplyFilters}
                  />
                </Col>
                <Col span={8}>
                  <Input
                    key={`doc-${filterKey}`}
                    ref={docInputRef}
                    placeholder="Doc. carico prodotto"
                    prefix={<SearchOutlined />}
                    onPressEnter={handleApplyFilters}
                  />
                </Col>
              </Row>
            </Col>
            <Col flex="none">
              <Space>
                <Checkbox
                  checked={filterAvailability}
                  onChange={(e) => setFilterAvailability(e.target.checked)}
                >
                  Solo con disponibilità
                </Checkbox>
                <Checkbox
                  checked={filterReservations}
                  onChange={(e) => setFilterReservations(e.target.checked)}
                >
                  Prenotazioni
                </Checkbox>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleApplyFilters}>
                  Filtra
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                  Pulisci
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Tabella Inventario */}
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            scroll={{ x: 2460 }}
            pagination={{
              pageSize: pageSize,
              onShowSizeChange: (_current, size) => setPageSize(size),
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} di ${total} stock`,
              pageSizeOptions: ['50', '100', '200', '500'],
            }}
            size="small"
          />

          {inventoryData.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">
                Nessun stock trovato per i prodotti di questo ordine
              </Text>
            </div>
          )}
        </Spin>
      </Modal>

      {/* Modal Visualizzazione Prenotazioni */}
      <StockReservationsModal
        visible={reservationsModalVisible}
        stockId={selectedStock?.id || null}
        stockCode={selectedStock?.stock_code}
        productDescription={selectedStock?.product_description}
        onClose={handleCloseReservationsModal}
      />

      {/* Modal Prenotazione Multi-Fonte */}
      <MultiSourceReservationModal
        visible={multiSourceModalVisible}
        onClose={handleCloseMultiSourceModal}
        onReservationComplete={handleReservationComplete}
        stock={selectedStock}
        productName={selectedStock ? generateCompositeIdentifier(
          selectedStock.internal_product_code,
          selectedStock.size,
          selectedStock.color
        ) + ' - ' + selectedStock.product_description : ''}
        orderId={order?.id || 0}
        requestedQuantity={selectedStock?.order_requested_quantity}
        productTotalReserved={selectedStock ? (productReservationTotals[selectedStock.product_id] || 0) : 0}
        orderUnitPrice={selectedStock?.order_unit_price}
        orderCustomerId={order?.customer_id}
        orderCustomerName={order?.customer?.company}
      />

      {/* Opziona tutti i disponibili: domanda se scegliere gli stock */}
      <Modal
        title="Opziona tutti i disponibili"
        open={bulkAskVisible}
        onCancel={() => setBulkAskVisible(false)}
        maskClosable={false}
        width={640}
        footer={[
          <Button key="cancel" onClick={() => setBulkAskVisible(false)} disabled={bulkReserving}>
            Annulla
          </Button>,
          <Button key="oldest" onClick={() => executeBulkReserve()} loading={bulkReserving}>
            No, prendi i più vecchi
          </Button>,
          <Button
            key="choose"
            type="primary"
            disabled={bulkReserving}
            onClick={() => { setBulkAskVisible(false); setBulkChoiceVisible(true); }}
          >
            Sì, scelgo io
          </Button>,
        ]}
      >
        <p>
          Verranno opzionati <b>{bulkSummary.quantity} pz</b> disponibili su <b>{bulkSummary.products} prodotti</b>.
        </p>
        <p>
          Per <b>{bulkChoiceProducts.length} prodotti</b> c'è più disponibile di quanto serve all'ordine.
          Vuoi scegliere tu da quali stock prenderli? Rispondendo <b>No</b> verranno presi gli stock arrivati per primi
          in inventario (data ingresso più vecchia).
        </p>
        {bulkSummary.notReservable.length > 0 && (
          <div>Prodotti non opzionabili:{renderNotReservable(bulkSummary.notReservable)}</div>
        )}
      </Modal>

      {/* Opziona tutti i disponibili: scelta degli stock per i prodotti in eccedenza */}
      <ReserveAvailableChoiceModal
        visible={bulkChoiceVisible}
        products={bulkChoiceProducts}
        autoProducts={bulkSummary.autoProducts}
        autoQuantity={bulkSummary.autoQuantity}
        submitting={bulkReserving}
        onCancel={() => setBulkChoiceVisible(false)}
        onConfirm={(allocations) => executeBulkReserve(allocations)}
      />

      {/* Copertura Ordine Stock (merce entrata a fronte dell'ordine) */}
      <StockOrderCoverageModal
        visible={coverageVisible}
        order={order}
        onClose={() => setCoverageVisible(false)}
        onCoverageChanged={loadOrderCompleteness}
      />
    </>
  );
};

export default OrderManagementModal;